/**
 * Dialog — Base UI Dialog primitive.
 *
 * Design system mapping (verified against admin.webex.com `md-modal`):
 *   • Backdrop: bg-black/60 (light + dark) — no blur
 *   • Popup:    bg-white dark:bg-webex-gray-950
 *               border 1px rgba(0,0,0,0.20) → border-webex-gray-300 dark:border-webex-gray-700
 *               rounded-xl (20px)
 *               shadow 0 8px 16px 0 rgba(0,0,0,0.20), 0 0 1px 0 rgba(0,0,0,0.20)
 *               max-h-[85vh] flex-col overflow-hidden
 *   • Sizes:    sm=max-w-md  md=max-w-[40rem] (640px, Webex default)  lg=max-w-3xl  xl=max-w-5xl
 *   • Header:   relative, padding 24/24/20, NO bottom border
 *               built-in close X positioned top-right (suppress with `hideClose`)
 *   • Title:    h2, 24px font-medium, leading-30, text-center,
 *               color rgba(0,0,0,0.95) → text-webex-gray-950 dark:text-webex-gray-50
 *   • Body:     padding 4/32/0 (top/x/bottom), 16px text, NO borders
 *   • Footer:   padding 16/24/24, justify-end, NO top border
 *
 * Compound parts:
 *   <Dialog.Root>
 *     <Dialog.Trigger render={<Button>Open</Button>} />
 *     <Dialog.Content size="md">
 *       <Dialog.Header>
 *         <Dialog.Title>Title</Dialog.Title>
 *         <Dialog.Description>Optional subtitle</Dialog.Description>
 *       </Dialog.Header>
 *       <Dialog.Body>...content...</Dialog.Body>
 *       <Dialog.Footer>
 *         <Button variant="primary">Done</Button>
 *       </Dialog.Footer>
 *     </Dialog.Content>
 *   </Dialog.Root>
 */
import * as React from "react"
import { Dialog as BaseDialog } from "@base-ui-components/react/dialog"
import { cva, type VariantProps } from "class-variance-authority"
import { cn } from "@lib/cn"

const Root = BaseDialog.Root
const Trigger = BaseDialog.Trigger
const Close = BaseDialog.Close

const Title = React.forwardRef<
  HTMLHeadingElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Title>
>(({ className, ...props }, ref) => (
  <BaseDialog.Title
    ref={ref}
    className={cn(
      "m-0 text-2xl font-medium leading-[30px] text-center",
      "text-webex-gray-950 dark:text-webex-gray-50",
      // leave space for the absolute close X on either side
      "px-6",
      className,
    )}
    {...props}
  />
))
Title.displayName = "Dialog.Title"

const Description = React.forwardRef<
  HTMLParagraphElement,
  React.ComponentPropsWithoutRef<typeof BaseDialog.Description>
>(({ className, ...props }, ref) => (
  <BaseDialog.Description
    ref={ref}
    className={cn(
      "m-0 mt-1 text-base text-center leading-6",
      "text-webex-gray-700 dark:text-webex-gray-300",
      className,
    )}
    {...props}
  />
))
Description.displayName = "Dialog.Description"

export const dialogContentVariants = cva(
  [
    "fixed left-1/2 top-1/2 z-50 w-full -translate-x-1/2 -translate-y-1/2",
    "rounded-xl border bg-white dark:bg-webex-gray-950",
    "border-webex-gray-300 dark:border-webex-gray-700",
    "shadow-[0_8px_16px_0_rgba(0,0,0,0.2),0_0_1px_0_rgba(0,0,0,0.2)]",
    "dark:shadow-[0_8px_16px_0_rgba(0,0,0,0.55),0_0_1px_0_rgba(255,255,255,0.12)]",
    "max-h-[85vh] flex flex-col overflow-hidden outline-none",
    // Webex uses an opacity-only fade — no scale on enter/exit.
    "data-[starting-style]:opacity-0",
    "data-[ending-style]:opacity-0",
    "transition-opacity duration-150 motion-reduce:transition-none",
  ].join(" "),
  {
    variants: {
      size: {
        sm: "max-w-md",
        md: "max-w-[40rem]", // 640px — Webex default
        lg: "max-w-3xl",
        xl: "max-w-5xl",
      },
    },
    defaultVariants: {
      size: "md",
    },
  },
)

interface ContentProps
  extends React.ComponentPropsWithoutRef<typeof BaseDialog.Popup>,
    VariantProps<typeof dialogContentVariants> {
  /** Render the backdrop. Default true. */
  withBackdrop?: boolean
}

const Content = React.forwardRef<HTMLDivElement, ContentProps>(
  ({ className, children, size, withBackdrop = true, ...props }, ref) => (
    <BaseDialog.Portal>
      {withBackdrop && (
        <BaseDialog.Backdrop
          className={cn(
            "fixed inset-0 z-40 bg-black/60",
            "data-[starting-style]:opacity-0 data-[ending-style]:opacity-0",
            "transition-opacity duration-150 motion-reduce:transition-none",
          )}
        />
      )}
      <BaseDialog.Popup
        ref={ref}
        className={cn(dialogContentVariants({ size }), className)}
        {...props}
      >
        {children}
      </BaseDialog.Popup>
    </BaseDialog.Portal>
  ),
)
Content.displayName = "Dialog.Content"

interface HeaderProps extends React.HTMLAttributes<HTMLDivElement> {
  /** Suppress the built-in close X. Default false. */
  hideClose?: boolean
  /** Override the close X aria-label. */
  closeLabel?: string
}

function Header({ className, children, hideClose, closeLabel = "Close", ...props }: HeaderProps) {
  return (
    <div
      className={cn(
        // padding: 24px 24px 20px (top/x/bottom). NO border.
        "relative flex flex-col gap-0 px-6 pt-6 pb-5",
        className,
      )}
      {...props}
    >
      {children}
      {!hideClose && (
        <BaseDialog.Close
          aria-label={closeLabel}
          className={cn(
            "absolute top-3 right-3 inline-flex size-7 items-center justify-center rounded",
            "text-webex-gray-800 dark:text-webex-gray-200",
            "hover:bg-webex-gray-100 dark:hover:bg-webex-gray-850",
            "focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-webex-blue-600",
          )}
        >
          <svg viewBox="0 0 16 16" className="size-4" fill="none" stroke="currentColor" strokeWidth="1.6" strokeLinecap="round" aria-hidden>
            <path d="M4 4l8 8M12 4l-8 8" />
          </svg>
        </BaseDialog.Close>
      )}
    </div>
  )
}

function Body({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // padding: 4px (mx 32px). 16px text. NO borders. Scrolls if content overflows.
        "px-8 pt-1 pb-0 overflow-y-auto",
        "text-base leading-6 text-webex-gray-950 dark:text-webex-gray-50",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

function Footer({ className, children, ...props }: React.HTMLAttributes<HTMLDivElement>) {
  return (
    <div
      className={cn(
        // padding: 16/24/24, justify-end. NO border.
        "flex items-center justify-end gap-2 px-6 pt-4 pb-6",
        className,
      )}
      {...props}
    >
      {children}
    </div>
  )
}

export const Dialog = {
  Root,
  Trigger,
  Content,
  Header,
  Title,
  Description,
  Body,
  Footer,
  Close,
}
